import React, { useMemo } from "react";
import { Typography } from "@mui/material";

const FORMATTING_TAGS = new Set([
  "div", "p", "span", "strong", "b", "em", "i", "u", "s", "br",
  "ul", "ol", "li", "blockquote", "h1", "h2", "h3", "h4", "h5", "h6",
  "pre", "code", "small", "sub", "sup",
]);
const DISCARD_CONTENT_TAGS = new Set([
  "script", "style", "iframe", "object", "embed", "svg", "math", "form",
  "template", "noscript", "head", "title", "link", "meta", "base",
  "input", "textarea", "select", "button", "audio", "video", "source", "track",
]);

function numericStyle(value, units, minimum, maximum) {
  const match = new RegExp(`^(\\d+(?:\\.\\d+)?|\\.\\d+)(${units})$`).exec(value);
  if (!match) return undefined;
  const number = Number(match[1]);
  return number >= minimum && number <= maximum ? value : undefined;
}

function formattingStyle(element) {
  const fontSize = element.style.getPropertyValue("font-size").trim().toLowerCase();
  const lineHeight = element.style.getPropertyValue("line-height").trim().toLowerCase();
  const safeFontSize = numericStyle(fontSize, "px", 8, 48)
    || numericStyle(fontSize, "em|rem", 0.5, 3);
  const safeLineHeight = numericStyle(lineHeight, "", 0.8, 3)
    || numericStyle(lineHeight, "px", 12, 96);
  return {
    ...(safeFontSize ? { fontSize: safeFontSize } : {}),
    ...(safeLineHeight ? { lineHeight: safeLineHeight } : {}),
  };
}

function formattedNodes(html) {
  if (typeof document === "undefined") return html;
  // Template contents stay inert. Only fresh React elements below reach the page.
  const template = document.createElement("template");
  template.innerHTML = html;

  const convert = (node, key) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const tag = node.localName;
    if (
      node.namespaceURI !== "http://www.w3.org/1999/xhtml"
      || DISCARD_CONTENT_TAGS.has(tag)
    ) return null;
    const children = Array.from(node.childNodes, (child, index) => convert(child, `${key}.${index}`));
    if (!FORMATTING_TAGS.has(tag)) return children;
    const props = { key, style: formattingStyle(node) };
    return tag === "br"
      ? React.createElement(tag, props)
      : React.createElement(tag, props, children);
  };
  return Array.from(template.content.childNodes, (node, index) => convert(node, String(index)));
}

export default function NewsContent({ details, detailsLanguage }) {
  const text = typeof details === "string" ? details : "";
  const isHtml = detailsLanguage === 1;
  const content = useMemo(() => isHtml ? formattedNodes(text) : text, [isHtml, text]);

  return (
    <Typography
      component="div"
      variant="body2"
      sx={{
        mt: 1,
        whiteSpace: isHtml ? "normal" : "pre-wrap",
        overflowWrap: "anywhere",
        "& p": { m: "0 0 0.75em" },
        "& p:last-child": { mb: 0 },
        "& pre": { whiteSpace: "pre-wrap" },
      }}
    >
      {content}
    </Typography>
  );
}
